import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import LoadingStates from "../constants/states/LoadingStates";
import SecureBaseStore from "./SecureBaseStore";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";


class MemberState {
    constructor(
        loadingState,
        memberId,
        authSystemId,
        authSystemName,
        authSystemUserId,
        profileCreated,
        emailVerified,
        smsVerified,
        created,
        updated,
        deleted
    ) {
        this.loadingState = loadingState;
        this.memberId = memberId;
        this.authSystemId = authSystemId;
        this.authSystemName = authSystemName;
        this.authSystemUserId = authSystemUserId;
        this.profileCreated = profileCreated;
        this.emailVerified = emailVerified;
        this.smsVerified = smsVerified;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
    }

    equals(other) {
        return this.loadingState === other.loadingState &&
            this.memberId === other.memberId &&
            this.authSystemId === other.authSystemId &&
            this.authSystemName === other.authSystemName &&
            this.authSystemUserId === other.authSystemUserId &&
            this.profileCreated === other.profileCreated &&
            this.emailVerified === other.emailVerified &&
            this.smsVerified === other.smsVerified &&
            this.created === other.created &&
            this.updated === other.updated &&
            this.deleted === other.deleted;
    }
}

class ImmutableMemberStateBuilder {

    constructor() {
        this.loadingState = LoadingStates.INITIALIZED;
        this.memberId = -1;
        this.authSystemId = -1;
        this.authSystemName = "";
        this.authSystemUserId = -1;
        this.profileCreated = false;
        this.emailVerified = false;
        this.smsVerified = false;
        this.created = 0;
        this.updated = 0;
        this.deleted = true;
    }

    static fromState(state) {
        return new ImmutableMemberStateBuilder()
            .setLoadingState(state.loadingState)
            .setMemberState(
                state.memberId,
                state.authSystemId,
                state.authSystemName,
                state.authSystemUserId,
                state.profileCreated,
                state.emailVerified,
                state.smsVerified,
                state.created,
                state.updated,
                state.deleted
            );
    }

    setLoadingState(loadingState) {
        this.loadingState = loadingState;
        return this;
    }

    setProfileCreated(profileCreated) {
        this.profileCreated = profileCreated;
        return this;
    }

    setEmailVerified(emailVerified) {
        this.emailVerified = emailVerified;
        return this;
    }

    setPhoneNumberVerified(smsVerified) {
        this.smsVerified = smsVerified;
        return this;
    }

    setMemberState(
        memberId,
        authSystemId,
        authSystemName,
        authSystemUserId,
        profileCreated,
        emailVerified,
        smsVerified,
        created,
        updated,
        deleted
    ) {
        this.loadingState = LoadingStates.FINISHED_LOADING;
        this.memberId = memberId;
        this.authSystemId = authSystemId;
        this.authSystemName = authSystemName;
        this.authSystemUserId = authSystemUserId;
        this.profileCreated = profileCreated;
        this.emailVerified = emailVerified;
        this.smsVerified = smsVerified;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
        return this;
    }

    build() {
        return Object.freeze(
            new MemberState(
                this.loadingState,
                this.memberId,
                this.authSystemId,
                this.authSystemName,
                this.authSystemUserId,
                this.profileCreated,
                this.emailVerified,
                this.smsVerified,
                this.created,
                this.updated,
                this.deleted));
    }
}


class MemberStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.MEMBER_STORE_UPDATED, ImmutableMemberStateBuilder);
        this.subscribeToEvent(ActionResultEvents.SESSION_CREATED, this.handleMemberLoaded);
        this.subscribeToEvent(ActionResultEvents.MEMBER_LOADED, this.handleMemberLoaded);
        this.subscribeToEvent(UIInteractionEvents.RENDERED_VIEW_REQUIRES_MEMBER, this.handleLoadingStart);
        this.subscribeToEvent(ActionResultEvents.PROFILE_CREATED, this.handleProfileCreated);
        this.subscribeToEvent(ActionResultEvents.PROFILE_EDITED, this.resetStore);
        this.subscribeToEvent(ActionResultEvents.EMAIL_VERIFICATION_SUCCESS, this.handleEmailVerified);
        this.subscribeToEvent(ActionResultEvents.PHONE_NUMBER_VERIFICATION_SUCCESS, this.handlePhoneNumberVerified);
    }

    handlePhoneNumberVerified = (topic, data) => {
        return ImmutableMemberStateBuilder.fromState(this.state)
            .setPhoneNumberVerified(true)
            .build()
    };

    handleEmailVerified = (topic, data) => {
        return ImmutableMemberStateBuilder.fromState(this.state)
            .setEmailVerified(true)
            .build()
    };

    handleProfileCreated = (topic, data) => {
        return ImmutableMemberStateBuilder.fromState(this.state)
            .setProfileCreated(true)
            .build()
    };

    handleLoadingStart = (topic, data) => {
       return new ImmutableMemberStateBuilder()
           .setLoadingState(LoadingStates.LOADING)
           .build()
    };

    handleMemberLoaded = (topic, data) => {
        return new ImmutableMemberStateBuilder()
            .setMemberState(
                data.memberId,
                data.authSystemId,
                data.authSystemName,
                data.authSystemUserId,
                data.profileCreated,
                data.emailVerified,
                data.smsVerified,
                data.created,
                data.updated,
                data.deleted)
            .build();
    };
}

export default new MemberStore();